/* eslint-disable no-underscore-dangle */
/* eslint-disable no-param-reassign */
// TODO: finish js docs and double check that they are all correct and up-to-date
import {
  cloneDeep,
  merge,
  get,
  has,
} from 'lodash-es';
// eslint-disable-next-line import/no-unresolved
import postUtils from '@tagboard/helpers/post-utils';

/**
 * Vue 3-compatible nested object updates.
 */
import {
  setOne,
  setMany,
  deleteOne,
  deleteMany,
} from '../../../utils/objectPath';

/**
 * Default elements object.
 * @constant
 * @type {object}
 * @see {@link ../../utils/defaults}
 */
import defaultElements from '../../../utils/defaults';

/**
 * Utility functions for element positioning and rendering.
 *
 * - `needsPixelArray`: Array of CSS properties that require pixel values.
 *    `hasChildren`: Checks if a given element ID has children within a group map object.
 * - `computeGroupSizeAndPosition`: Used to calculate the bounding size and position
 *     for a group of elements.
 * - `getPositionRelativeToGroupParent`: Determines an element's position relative
 *     to its group parent.
 */
import {
  needsPixelArray,
  hasChildren,
  computeGroupSizeAndPosition,
  getPositionRelativeToGroupParent,
} from './utilities';

import toast from '../../../utils/toasts';
import { socialNetworkBranding } from '../../../utils/social';
import ConduitEventsService from '../../../services/conduit';
import { getMediaSizes } from '../../../utils/mediaSize';
import { buildImageUrl } from '../../../utils/useMedia';
const TGB_ASSETS_URL = process.env.TGB_ASSETS_CDN_URL;

/**
 * Represents the elements state.
 * @typedef {object} ElementsState
 * @property {object} elementGroupMap - Map of element groups.
 * @property {object} elementsById - Map of elements by ID.
 * @property {object || null} queuedElement - Element to be added on canvas click.
 */
export const elementsState = {
  elementGroupMap: {
    root: [],
  },
  elementsById: {},
  queuedElement: null,
  mediaSizeSummary: {
    staticImages: { totalBytes: 0, knownCount: 0, unknownCount: 0 },
    videos: { totalBytes: 0, knownCount: 0, unknownCount: 0 },
    mappedMedia: { totalBytes: 0, knownCount: 0, unknownCount: 0 },
    loading: false,
    error: null,
    lastUpdated: null,
  },
};

/**
 * Elements getters object.
 * @type {object}
 */
export const elementsGetters = {
  /**
   * Checks if a given element, identified by its ID, has an associated data provider.
   *
   * This getter evaluates whether an element within the Vuex state, identified by its ID,
   * has a `data.provider` property. It effectively determines if the element has been
   * "mapped" to a data provider.
   *
   * @function
   * @name checkIfMapped
   * @param {Object} state - The Vuex state object.
   * @returns {Function} A function that takes an element ID and returns a boolean.
   */
  checkIfMapped: (state) => (id) => !!state.elementsById[id]?.data?.provider,

  isDataProvider: (state) => (id) => ['sheet-data', 'sportradar', 'social-data', 'shoppable'].includes(state.elementsById[id]?.type),

  getDataProvider: (state, getters) => (id) => {
    // provider defined on element (already or previously mapped)
    if (state.elementsById[id]?.data?.provider) {
      const pid = state.elementsById[id]?.data?.provider;
      if (getters.isDataProvider(pid)) {
        return state.elementsById[pid];
      }
    }

    // check parents to see if they are a provider (i.e. element that was dragged into a provider)
    const parents = getters.getAllParents(id);
    const found = parents.find((parentId) => getters.isDataProvider(parentId));

    return state.elementsById[found] ?? null;
  },

  /**
   * Retrieves an element from the Vuex state based on its ID.
   *
   * This getter provides a way to access specific elements stored in the Vuex state
   * using their unique identifiers. If the element with the given ID does not exist,
   * it returns `undefined`.
   *
   * @function
   * @name getElementById
   * @param {Object} state - The Vuex state object containing the `elementsById` map.
   * @returns {Function} A function that takes an element ID and returns the corresponding
   *      element or `undefined` if not found.
   */
  getElementById: (state) => (id) => state.elementsById[id],

  /**
   * Find an element in graphic where fn returns true.
   * @param {Object} state - The Vuex state object.
   * @returns {Function} - A function that takes a predicate function
   *  and returns the first matching element.
   */
  findElement: (state) => (fn) => Object.values(state.elementsById).find(fn),

  /**
   * Retrieves a nested structure of elements based on their hierarchical relationship.
   *
   * This getter constructs a hierarchical view of elements from the Vuex state. Starting
   * from the root, it recursively fetches elements and their nested children. If an element
   * has associated child elements in the `elementGroupMap`, those children are recursively
   * fetched and nested within their parent element's `elements` property.
   *
   * If there are no elements in the root of the `elementGroupMap`, an empty array is returned.
   *
   * @function
   * @name getNestedElements
   * @param {Object} state - The Vuex state object containing `elementsById` and `elementGroupMap`.
   * @returns {Object[]} An array of elements, where each element may have a nested `elements`
   *      property containing its child elements.
   */
  getNestedElements: (state) => {
    // not sure this should be computed all the time long term, but leaving it as a getter for now
    // because it makes the output visible in vue devtools so easily
    // maybe it should prob be an helper function instead so it only runs on demand
    if (!state.elementGroupMap.root?.length) return []; // jet if we don't have elements
    const buildElements = (ids) => ids.map((id) => {
      const element = cloneDeep(state.elementsById[id]);
      if (Object.keys(state.elementGroupMap).includes(element.id)) {
        return {
          ...element,
          elements: buildElements(state.elementGroupMap[element.id]),
        };
      }
      return {
        ...element,
      };
    });

    const rootIds = state.elementGroupMap.root;
    return buildElements(rootIds);
  },

  /**
   * Retrieves the parent group ID of a given element based on its ID.
   *
   * This getter searches through the `elementGroupMap` of the Vuex state to determine
   * the parent group ID in which a specific element resides. If the element doesn't belong
   * to any group or the group is not found, it returns `null`.
   *
   * @function
   * @name getElementParentGroupId
   * @param {Object} state - The Vuex state object containing the `elementGroupMap`.
   * @returns {Function} A function that takes an element ID and returns the parent
   *      group ID or `null` if not found.
   */
  getElementParentGroupId: (state) => (elementId) => {
    const parentEntry = Object.entries(state.elementGroupMap)
      .find(([key, value]) => value.includes(elementId));
    return parentEntry ? parentEntry[0] : null;
  },

  /**
   * Retrieves all parent group IDs of a given element based on its ID.
   *
   * This getter recursively searches through the `elementGroupMap` of the Vuex state
   * to identify and return all parent group IDs in which the specified element resides.
   * If the element doesn't have any parents, it returns an empty array.
   *
   * @function
   * @name getAllParents
   * @param {Object} state - The Vuex state object containing the `elementGroupMap`.
   * @returns {Function} A function that takes an element ID and returns an array
   *      of all parent group IDs. Returns an empty array if no parents are found.
   */
  getAllParents: (state) => (elementId) => {
    const parents = [];

    function findParents(element) {
      Object.keys(state.elementGroupMap).forEach((parent) => {
        if (state.elementGroupMap[parent].includes(element)) {
          parents.push(parent);
          findParents(parent);
        }
      });
    }

    findParents(elementId);
    return parents;
  },

  /**
   * Retrieves the transform styles (skew and rotate) for a given element based on its ID.
   *
   * This getter constructs a CSS transform string for skewing and rotating an element
   * using the respective skewX, skewY, and rotate properties from the `elementsById` object
   * in the Vuex state.
   *
   * @function
   * @name getTransformStyles
   * @param {Object} state - The Vuex state object containing the `elementsById`.
   * @returns {Function} A function that takes an element ID and returns an object with
   *      a `transform` property containing the CSS transform string.
   */
  getTransformStyles: (state) => (id) => {
    const element = state.elementsById[id];
    if (!element) {
      return {};
    }

    const skewX = `${element.skewX ?? 0}deg`;
    const skewY = `${element.skewY ?? 0}deg`;
    const rotate = `${element.rotate ?? 0}deg`;
    const scaleX = element.flipX ? -1 : 1;
    const scaleY = element.flipY ? -1 : 1;

    return {
      transform: `skew(${skewX}, ${skewY}) rotate(${rotate}) scale(${scaleX}, ${scaleY})`,
    };
  },

  /**
   * Retrieves the border radius styles for a given element based on its ID.
   *
   * This getter constructs a CSS border-radius string using the respective
   * borderTopLeftRadius, borderTopRightRadius, borderBottomRightRadius, and
   * borderBottomLeftRadius properties from the `elementsById` object in the Vuex state.
   *
   * @function
   * @name getBorderRadiusStyles
   * @param {Object} state - The Vuex state object containing the `elementsById`.
   * @returns {Function} A function that takes an element ID and returns an object with
   *      a `borderRadius` property containing the CSS border-radius string.
   */
  getBorderRadiusStyles: (state) => (id) => {
    const element = state.elementsById[id];
    if (!element) {
      return {};
    }

    return {
      borderRadius: `${element.borderTopLeftRadius}px ${element.borderTopRightRadius}px ${element.borderBottomRightRadius}px ${element.borderBottomLeftRadius}px`,
    };
  },

  /**
   * Retrieves the background image URL for a given element based on its ID.
   *
   * This getter determines the background image URL of an element using the `backgroundImageUrl`
   * and `data` properties from the `elementsById` object in the Vuex state. It checks for newer
   * and older ways of storing this data. If the `data` property has a `provider`, it first checks
   * for the newer method using `dataKeyMap`. If not available, it checks the older method using
   * `dataValue`. If neither provides a valid URL, it returns the `backgroundImageUrl` directly.
   *
   * @function
   * @name getBackgroundImageUrl
   * @param {Object} state - The Vuex state object with `elementsById`.
   * @param {Object} getters - Vuex getters.
   * @returns {Function} A function that takes an element ID and returns the background image URL.
   */
  getBackgroundImageUrl: (state, getters) => (id) => {
    const { backgroundImageUrl, data } = state.elementsById[id] || {};

    if (data?.provider) {
      // "new" way
      if (getters.isPropertyMapped(id, 'backgroundImageUrl')) {
        return getters.getDataValue(id, 'backgroundImageUrl');
      }
    }

    return backgroundImageUrl;
  },

  // TODO: update docs once all TODOs in this block are done
  /**
   * Get background styles.
   * @param {Object} state - The state object.
   * @returns {Function} - A function to generate background styles based on a fallback flag.
   */
  /**
   * Generate background styles based on a fallback flag.
   * @param {String} id - The element id.
   * @param {boolean} useFallback - Flag indicating whether to use fallback background image.
   * @returns {Object} - The background styles object.
   */
  getBackgroundStyles: (state, getters) => (id, useFallback) => {
    const element = state.elementsById[id];
    if (!element) {
      return {};
    }

    const {
      backgroundSize, // string
      backgroundImage: useBackgroundImage, // boolean
      gradientDirection, // number
      fillStyle, // string
      backgroundPosition, // string
    } = element;

    let {
      backgroundColor, // string
      gradientColorOne, // string
      gradientColorTwo, // string
    } = element;

    // Override colors if they are mapped and provided by data.
    if (element?.data?.provider) {
      if (getters.isPropertyMapped(id, 'backgroundColor')) backgroundColor = getters.getDataValue(id, 'backgroundColor');
      if (getters.isPropertyMapped(id, 'gradientColorOne')) gradientColorOne = getters.getDataValue(id, 'gradientColorOne');
      if (getters.isPropertyMapped(id, 'gradientColorTwo')) gradientColorTwo = getters.getDataValue(id, 'gradientColorTwo');
    }

    const optimizeBackgroundImageUrl = useBackgroundImage && !useFallback
      ? getters.getBackgroundImageUrl(id)
      : element.backgroundImageUrl;

    const isMappedBackgroundImage = !useFallback && getters.isPropertyMapped(id, 'backgroundImageUrl');
    const backgroundImageUrl = optimizeBackgroundImageUrl && !isMappedBackgroundImage
      ? buildImageUrl(optimizeBackgroundImageUrl, { ...element.backgroundImageMeta })
      : optimizeBackgroundImageUrl;

    // Construct background image and gradient styles.
    const backgroundImageUrlStyle = backgroundImageUrl ? `url('${backgroundImageUrl}')` : 'none';
    const gradientStyle = fillStyle === 'gradient' ? `linear-gradient(${gradientDirection}deg, ${gradientColorOne}, ${gradientColorTwo})` : null;

    // styles used for background image
    const imageStyles = useBackgroundImage ? {
      backgroundImage: backgroundImageUrlStyle,
      backgroundSize,
      backgroundPosition,
    } : {};

    // styles used for gradient
    const backgroundImage = useBackgroundImage && backgroundImageUrl
      ? `${backgroundImageUrlStyle}, ${gradientStyle}`
      : gradientStyle;
    const gradientStyles = gradientStyle ? { backgroundImage } : {};

    // styles used for solid color (not gradient)
    const colorStyles = fillStyle !== 'gradient' ? { backgroundColor } : {};

    // Merge and return the final styles object.
    return {
      ...imageStyles,
      ...gradientStyles,
      ...colorStyles,
    };
  },

  /**
   * Retrieves the border styles for a given element based on its ID.
   *
   * This getter determines the border styles of an element using the properties
   * `border`, `borderWidth`, and `borderColor` from the `elementsById` object in
   * the Vuex state. If the element has mapped data points and a `dataKeyMap` for
   * `borderColor`, the color is overridden with the mapped value. If the `border`
   * property is truthy, a styles object with the formatted border style is returned.
   * Otherwise, an empty object is returned.
   *
   * @function
   * @name getBorderStyles
   * @param {Object} state - The Vuex state object containing the `elementsById`.
   * @param {Object} getters - Vuex getters.
   * @returns {Function} A function that takes an element ID and returns the border styles object.
   */
  getBorderStyles: (state, getters) => (id) => {
    const element = state.elementsById[id];
    if (!element) {
      return {};
    }

    const {
      border,
      borderWidth,
    } = element;

    let {
      borderColor,
    } = element;

    // handle mapped data points
    if (element?.data?.provider) {
      if (getters.isPropertyMapped(id, 'borderColor')) borderColor = getters.getDataValue(id, 'borderColor');
    }

    if (border) {
      return {
        border: `${borderWidth}px solid ${borderColor}`,
      };
    }

    return {};
  },

  /**
   * Generates CSS styles related to box-shadow or drop-shadow for a given element ID.
   *
   * If the element has the property `boxShadow` set, this function will generate the appropriate
   * CSS styles. The function supports both traditional `box-shadow` and the experimental
   * `drop-shadow` filter. Additionally, if the element has data mappings for the `boxShadowColor`,
   * this function fetches the mapped color value.
   *
   * @function
   * @name getBoxShadowStyles
   * @param {Object} state - The Vuex state object.
   * @param {Object} getters - The Vuex getters object.
   * @param {string} id - The ID of the element for which box-shadow styles are to be generated.
   * @returns {Object} An object containing CSS styles related to box-shadow or drop-shadow.
   * If no box-shadow property is set for the element, an empty object is returned.
   */
  getBoxShadowStyles: (state, getters) => (id) => {
    const element = state.elementsById[id];
    if (!element) {
      return {};
    }

    if (element.boxShadow) {
      const {
        boxShadowX, // number
        boxShadowY, // number
        boxShadowBlur, // number
        boxShadowSpread, // number
        dropShadow, // boolean
      } = element;

      let {
        boxShadowColor, // string
      } = element;

      // handle mapped data points
      if (element?.data?.provider) {
        if (getters.isPropertyMapped(id, 'boxShadowColor')) boxShadowColor = getters.getDataValue(id, 'boxShadowColor');
      }

      // Experimental box shadow uses CSS drop-shadow filter instead of box-shadow
      if (dropShadow) {
        const shadow = `${boxShadowX}px ${boxShadowY}px ${boxShadowBlur}px ${boxShadowColor}`;

        return {
          filter: `drop-shadow(${shadow})`,
        };
      }

      const shadow = `${boxShadowX}px ${boxShadowY}px ${boxShadowBlur}px ${boxShadowSpread}px ${boxShadowColor}`;

      return {
        boxShadow: shadow,
      };
    }

    return {};
  },

  /**
   * Generates CSS styles related to blur for a given element ID.
   *
   * If the element has the property `blur` set, this function will generate the appropriate
   * CSS filter styles to apply the blur effect.
   *
   * @function
   * @name getBlurStyles
   * @param {Object} state - The Vuex state object.
   * @param {string} id - The ID of the element for which blur styles are to be generated.
   * @returns {Object} An object containing CSS styles related to blur.
   * If no blur property is set for the element, an empty object is returned.
   */
  getBlurStyles: (state) => (id) => {
    const element = state.elementsById[id];
    if (!element) {
      return {};
    }

    const { blur, blurSize } = element;
    if (blur) {
      return {
        filter: `blur(${blurSize}px)`,
      };
    }

    return {};
  },

  /**
   * Generates CSS styles related to flex gap for a given element ID.
   * @function
   * @name getGapStyles
   * @param {Object} state - The Vuex state object.
   * @param {string} id - The ID of the element for which gap styles are to be generated.
   * @returns {Object} An object containing CSS styles related to gap.
   * If no gap property is set for the element, an empty object is returned.
   * */
  getGapStyles: (state) => (id) => {
    const element = state.elementsById[id];
    if (!element) {
      return {};
    }

    const { gap } = element.styles;
    if (gap) {
      return {
        gap: `${gap}px`,
      };
    }

    return {};
  },

  /**
   * Generates basic styles for a given element ID based on the state.
   *
   * This function processes the styles object of the given element and
   * checks for any mapped data values. It also checks if the style key
   * requires pixel values and formats the value accordingly.
   *
   * @function
   * @name getBasicStyles
   * @param {Object} state - The Vuex state object.
   * @param {Function} getters - Vuex getters.
   * @param {string} id - The ID of the element for which styles are to be generated.
   * @returns {Object} An object containing the processed styles for the element.
   */
  getBasicStyles: (state, getters, rootState) => (id) => {
    const element = state.elementsById[id];
    if (!element) {
      return {};
    }

    const result = {};
    const { styles } = element;
    Object.keys(styles).forEach((key) => {
      let value = styles[key];

      if (element?.data?.provider) {
        if (getters.isPropertyMapped(id, key)) value = getters.getDataValue(id, key);
      }

      result[key] = needsPixelArray.includes(key) && value !== 'auto'
        ? `${value}px`
        : value;
    });

    return { ...result, 'outline-width': `${0.15 / rootState.panAndZoom.scaleFactor}rem` };
  },

  /**
   * Generates text shadow styles for a given element ID based on the state.
   *
   * This function extracts the text shadow properties of the specified element,
   * checks for mapped data values, and then compiles these properties into a
   * CSS `textShadow` string if the text shadow is enabled.
   *
   * @function
   * @name getTextShadowStyles
   * @param {Object} state - The Vuex state object.
   * @param {Function} getters - Vuex getters.
   * @param {string} id - The ID of the element for which text shadow styles are to be generated.
   * @returns {Object} An object containing the compiled text shadow style or an empty object if
   *          text shadow is not enabled for the element.
   */
  getTextShadowStyles: (state, getters) => (id) => {
    const element = state.elementsById[id];
    if (!element) {
      return {};
    }

    const {
      textShadow,
      textShadowBlur,
      textShadowX,
      textShadowY,
    } = element;

    let {
      textShadowColor,
    } = element;

    if (element?.data?.provider) {
      if (getters.isPropertyMapped(id, 'textShadowColor')) textShadowColor = getters.getDataValue(id, 'textShadowColor');
    }

    if (textShadow) {
      return {
        textShadow: `${textShadowX}px ${textShadowY}px ${textShadowBlur}px ${textShadowColor}`,
      };
    }

    return {};
  },

  /**
   * Generates background styles for a given element ID based on the state.
   *
   * This function retrieves the background properties of the specified element,
   * checks for mapped data values, and then assembles these properties into
   * CSS style values. Depending on the fill style, it might produce a gradient
   * background or a solid color background.
   *
   * @function
   * @name getTextBackgroundStyles
   * @param {Object} state - The Vuex state object.
   * @param {Function} getters - Vuex getters.
   * @param {string} id - The ID of the element for which background styles are to be generated.
   * @returns {Object} An object containing the computed background style or an empty object if
   *          the background is not set for the element.
   */
  getTextBackgroundStyles: (state, getters) => (id) => {
    const element = state.elementsById[id];
    if (!element) {
      return {};
    }

    const {
      background,
      fillStyle,
      gradientDirection,
    } = element;

    let {
      backgroundColor, // string
      gradientColorOne, // string
      gradientColorTwo, // string
    } = element;

    // handle mapped data points
    if (element?.data?.provider) {
      if (getters.isPropertyMapped(id, 'backgroundColor')) backgroundColor = getters.getDataValue(id, 'backgroundColor');
      if (getters.isPropertyMapped(id, 'gradientColorOne')) gradientColorOne = getters.getDataValue(id, 'gradientColorOne');
      if (getters.isPropertyMapped(id, 'gradientColorTwo')) gradientColorTwo = getters.getDataValue(id, 'gradientColorTwo');
    }

    if (background) {
      if (fillStyle === 'gradient') {
        return {
          backgroundImage: `linear-gradient(${gradientDirection}deg, ${gradientColorOne}, ${gradientColorTwo})`,
        };
      }

      return { backgroundImage: 'none', backgroundColor };
    }

    return {};
  },

  getPaddingStyles: (state) => (id) => {
    const element = state.elementsById[id];
    if (!element) {
      return {};
    }

    const { padding = null } = element;

    if (padding) {
      return {
        padding: `${padding.top}px ${padding.right}px ${padding.bottom}px ${padding.left}px`,
      };
    }

    return {};
  },

  /**
   * Retrieves the looping animation style class for a given element ID from the state.
   *
   * @function
   * @name getLoopingClass
   * @param {Object} state - The Vuex state object.
   * @param {string} id - The ID of the element.
   * @returns {string|null} The looping animation style class or null if not found.
   */
  getLoopingClass: (state) => (id) => state.elementsById[id]?.loopingAnimationStyle ?? null,

  /**
   * Computes the animation styles for an element given its ID, whether it's looping,
   * and an animation toggle.
   *
   * @function
   * @name getAnimationStyles
   * @param {Object} state - The Vuex state object.
   * @param {string} id - The ID of the element.
   * @param {boolean} isLooping - Determines if the animation is looping.
   * @param {boolean} toggle - Toggles between the animation in and out styles.
   * @returns {Object} Object containing the computed animation styles.
   */
  getAnimationStyles: (state) => (id, isLooping, toggle) => {
    const element = state.elementsById[id];
    if (!element) {
      return {};
    }

    const {
      animation,
      loopingAnimationDuration,
      loopingAnimationEasing,
      animationInDelay,
      animationOutDelay,
      animationInEasing,
      animationOutEasing,
      animationInDuration,
      animationOutDuration,
    } = element;

    if (!animation) {
      return {};
    }

    if (isLooping) {
      return {
        animationDuration: `${loopingAnimationDuration}ms`,
        animationTimingFunction: `cubic-bezier(${loopingAnimationEasing})`,
      };
    }

    if (toggle) {
      return {
        animationDuration: `${animationInDuration}ms`,
        animationDelay: `${animationInDelay}ms`,
        animationTimingFunction: `cubic-bezier(${animationInEasing})`,
      };
    }

    return {
      animationDuration: `${animationOutDuration}ms`,
      animationDelay: `${animationOutDelay}ms`,
      animationTimingFunction: `cubic-bezier(${animationOutEasing})`,
    };
  },

  /**
   * Retrieves the value of a specified property from an element in the `elementsById` map.
   *
   * @function
   * @name getElementValueByProp
   * @param {Object} state - The Vuex state object.
   * @param {string|number} id - The ID of the element.
   * @param {string} propKey - The key of the property to retrieve from the element.
   * @returns {any|null} The value of the specified property or `null` if not found.
   */
  getElementValueByProp: (state) => (id, propKey) => state.elementsById[id]?.[propKey] || null,

  // TODO: make docs once the todos are completed in this block
  isPropertyMapped: (state, getters) => (id, property) => {
    const element = getters.getElementById(id);

    if (!has(element, 'data.provider')) return false;

    // checks for old sheets
    if (has(element, 'data.columnIdx') && ['text', 'backgroundImageUrl', 'permalink'].includes(property)) {
      return (has(element.data.dataValue, property) || element.data.dataValue === null);
    }
    // Check 0 for sheets column index
    return !!element.data?.dataKeyMap?.[property] || element.data?.dataKeyMap?.[property] === 0;
  },

  // TODO: docs once ddg stuff is fully converted
  getDataValue: (state, getters) => (elementId, property) => {
    const element = getters.getElementById(elementId);
    const providerEl = getters.getDataProvider(elementId);

    // The element or its provider may have been removed while Vue is still
    // re-rendering (e.g. deleting a layer), so bail out instead of throwing.
    if (!providerEl) {
      return null;
    }

    switch (providerEl.type) {
      case 'sportradar':
        return getters.getSportradarDataValue(element, providerEl, property);
      case 'sheet-data':
        return getters.getSheetDataValue(element, providerEl, property);
      case 'social-data':
        return getters.getSocialDataValue(element, providerEl, property);
      case 'shoppable':
        return getters.getShoppableDataValue(element, providerEl, property);

      default:
        return null;
    }
  },

  getSocialDataValue: (state, getters, rootState, rootGetters) => (el, provider, property) => {
    const { dataKeyMap } = el.data;
    const pathKey = dataKeyMap[property];
    const { posts, displayedPostIndex } = rootState[provider.id];
    const currentPost = posts[displayedPostIndex];
    const { network } = currentPost;
    // TODO: In future we may build in a way for them to control this content, but for now
    const verifiedIcon = (color) => `&nbsp<span class="fas fa-badge-check tgb-social-verified-icon" style="color:${color}"></span>`;
    // TODO: In future we may build in a way for them to control this content, but for now
    const atMention = ['twitch', 'share-it', 'youtube'].includes(network) ? '' : '<span class="tgb-social-at-mention">@</span>';

    let image;
    let videoPoster;
    let video;

    if (currentPost.photos && currentPost.photos.length) {
      image = currentPost.photos[0].l || currentPost.photos[0].m;
      if (currentPost.network === 'instagram') {
        image = postUtils.instagramMediaUrl(currentPost, 'l');
      }
    }

    if (currentPost.videos && currentPost.videos.length) {
      videoPoster = currentPost.videos[0].p;
      if (currentPost.network === 'facebook') {
        // because of how FB emdeds work, we need to build the right URL
        // personally I think if the URL of currentPost.videos[0] in our API doesn't work
        // we should build this URL correctly on the backend, but since we do this on the front
        // end in live, I'm doing it here.
        const pid = currentPost.post_id.split('_');
        const baseUrl = 'https://www.facebook.com/watch?v=';
        video = baseUrl + pid[1] || pid[0];
      } else {
        video = currentPost.videos ? currentPost.videos[0].m : null;
        if (currentPost.network === 'instagram') {
          videoPoster = postUtils.instagramMediaUrl(currentPost, 'l');
          video = postUtils.instagramMediaUrl(currentPost, 'video');
        }
      }
    }

    let value = null;

    switch (pathKey) {
      case 'user_profile_image_url':
        value = currentPost.user_profile_image_url || `${TGB_ASSETS_URL}/placeholders/image-not-available.png`; // no data backup image graphics only;

        if (network === 'twitter') {
          value = value.replace('_normal', '_200x200');
        } else if (network === 'facebook' && value.includes('graph.facebook.com')) {
          value = `${value.split('?')[0]}?type=large`;
        }

        return value;
      case 'user_real_name':
        value = currentPost.user_real_name || 'No Data'; // no data backup text graphics only

        if (has(currentPost, 'service_meta.user_verified') && get(currentPost, 'service_meta.user_verified')) {
          const verifiedTypeColors = {
            business: '#e6c134',
            government: '#819baa',
          };
          const color = verifiedTypeColors[currentPost.service_meta.verified_type] || 'rgb(29, 155, 240)';
          value += verifiedIcon(color);
        }

        return `<div>${value}</div>`;
      case 'user_name':
        value = currentPost.user_name || 'No Data'; // no data backup text graphics only
        return `<div>${atMention}${value}</div>`;
      case 'html':
        value = currentPost.html || currentPost.text || 'No Data'; // no data backup text graphics only
        value = `<div class="social-ddg-text--html">${value}</div>`;

        if (currentPost.network === 'reddit') {
          return `<div class="social-ddg-text--title">${currentPost.title}</div>${value || ''}`;
        }

        return value;
      case 'media':
        if (currentPost.videos) {
          if (property === 'src') return video;
          if (property === 'isVideo') return true;
          if (property === 'poster') return videoPoster;
        } else {
          if (property === 'src') return image || `${TGB_ASSETS_URL}/placeholders/image-not-available.png`; // no data backup image graphics only
          if (property === 'isVideo') return false;
          if (property === 'poster') return null;
        }
        break;
      default:
        return get(socialNetworkBranding[network], pathKey) || value;
    }
    return value;
  },

  // TODO: docs once ddg stuff is fully converted
  getSheetDataValue: (state, getters, rootState, rootGetters) => (el, provider, property) => {
    const { columnIdx, iteration = 0, dataKeyMap } = el.data;
    // backwards compatible with old single property way
    const dataIdx = dataKeyMap?.[property] ?? columnIdx;

    // get data-list to get filtered data
    const dataListId = getters.getElementParentGroupId(el.id) || el.data.dataListId;
    if (!['data-list', 'marquee'].includes(getters.getElementById(dataListId).type)) return; // element must have been moved, bail

    const { filters } = getters.getElementById(dataListId).data;
    const filteredData = rootGetters[`${provider.id}/getFilteredData`](filters);

    const currentDataPoint = filteredData[iteration]?.[dataIdx];

    switch (property) {
      case 'backgroundImageUrl':
      case 'imageUrl':
        if (has(currentDataPoint, 'img') && currentDataPoint.img.indexOf('http') === 0) {
          return currentDataPoint.img;
        }
        // String conversion because text might be a number
        if (has(currentDataPoint, 'text') && `${currentDataPoint.text}`.indexOf('http') === 0) {
          return currentDataPoint.text;
        }
        // If neither img, or text is formatted as a URL, then ignore them
        return el[property];

      default:
        return currentDataPoint?.text ?? '';
    }
  },

  /**
   * Retrieves and formats data from Sportradar based on the specified
   * element, provider, and property.
   *
   * @param {Object} state - The Vuex state.
   * @param {Object} getters - The Vuex getters.
   * @param {Object} rootState - The root state of the Vuex store.
   * @param {Object} rootGetters - The root getters of the Vuex store.
   * @returns {Function} - A function that takes an element, provider, and property,
   * and returns the formatted value.
   *
   * @param {Object} el - The element containing data to retrieve.
   * @param {Object} provider - The data provider containing the ID for accessing data.
   * @param {string} property - The property name to retrieve from the data.
   * @returns {string|null} - The formatted value of the property or `null` if data is unavailable.
   *
   * @description
   * - Checks if data exists for the specified element (`el`) and provider (`provider.id`).
   * - Translates the path of the property using translation keys from `rootGetters`.
   * - If `isLabel` is true, returns a formatted label based on the
   *   element's `name` or `defaultLabel`.
   * - Retrieves the raw value for the specified property path from `srData`.
   * - Determines the format based on property-specific or schema-defined formats.
   * - Applies formatting for percentage, weight, height, and floating-point values.
   *
   * ### Format Types:
   * - **percent**: Formats as a percentage, adding `%`.
   * - **weight**: Formats as pounds with `lbs.` suffix.
   * - **height**: Formats as feet and inches (`X'Y"`).
   * - **float**: Renders with one decimal place.
   * - **default**: Returns a string, converting to `0` if not a valid number.
   *
   */
  getSportradarDataValue: (state, getters, rootState, rootGetters) => (el, provider, property) => {
    if (!el.data || !rootState[provider.id]) {
      return null; // no data, no state, bail
    }
    const { dataKeyMap, isLabel, labelOverride } = el.data;
    const srData = rootGetters[`${provider.id}/allSportradarData`];
    const translationKeys = rootGetters[`${provider.id}/translationKeys`];

    const { customLabels, customFormats, schemaInfo } = rootState[provider.id];

    // Replace path segments using translation keys
    const re = new RegExp(Object.keys(translationKeys).join('|'), 'gi');
    const path = dataKeyMap[property].replace(re, (matched) => translationKeys[matched]);

    // For label elements, only return the custom label for text properties
    // For color properties, continue processing to get the actual color value
    if (isLabel && property === 'text') {
      const defaultLabel = customLabels[property] ?? path.split('.').pop(); // defaults to last segment of path
      const nameLabelOverride = labelOverride ? el.name : ''; // if labelOverride is true, use the element name as the label
      return (nameLabelOverride || defaultLabel).split('_')
        .map((word) => word[0].toUpperCase() + word.substring(1))
        .join(' ');
    }

    const rawValue = get(srData, path);
    const parts = path.split('.');
    const key = parts.pop(); // key for the current property
    const parentPath = parts.join('.'); // path for the parent object
    const parentData = get(srData, parentPath); // parent object
    const typeName = parentData?.__typename; // e.g. 'TeamSeasonStatsAverageNFL'
    const numberValue = Number(rawValue);
    // Early return for specific properties that don't need formatting
    if (['imageUrl', 'backgroundImageUrl'].includes(property)) {
      return rawValue;
    }

    let format = customFormats[property] ?? false;

    if (!format) {
      const lowerKey = key.toLowerCase();
      const schemaType = get(schemaInfo[typeName], key); // e.g. 'Float'
      if (lowerKey.endsWith('_pct')) format = 'percent';
      else if (schemaType === 'Float') format = 'float';
      else if (lowerKey === 'weight') format = 'weight';
      else if (lowerKey === 'height') format = 'height';
    }

    const isValidNumber = typeof numberValue === 'number' && !Number.isNaN(numberValue);

    switch (format) {
      case 'percent':
        return isValidNumber
          ? `${(numberValue < 1 ? (numberValue * 100).toFixed(1) : numberValue.toFixed(1))}%`
          : '0.0%';
      case 'weight':
        return isValidNumber ? `${numberValue} lbs.` : '0 lbs.';
      case 'height':
        return isValidNumber
          ? `${Math.floor(numberValue / 12)}'${Math.round(numberValue % 12)}"`
          : '0\'0"';
      case 'float':
        return isValidNumber ? numberValue.toFixed(1) : '0.0';
      default:
        if (isValidNumber) return numberValue.toString();
        if (typeof rawValue === 'string') return rawValue; // if "stat" is a string value (e.g. playerPosition), just return it
        return '0';
    }
  },

  getShoppableDataValue: (state, getters, rootState, rootGetters) => (el, provider, property) => {
    const { dataKeyMap } = el.data;
    const { id: providerId } = provider;
    const pathKey = dataKeyMap[property];

    // data
    const { url, name, image } = rootGetters[`${providerId}/getProduct`];
    const formattedPrice = rootGetters[`${providerId}/getFormattedProductPrice`];
    const termsText = rootGetters[`${providerId}/getTermsText`];

    switch (pathKey) {
      case 'productName':
        return name;

      case 'productPrice':
        return formattedPrice;

      case 'productImage':
        return image;

      case 'termsText':
        return termsText;

      default:
        return url; // affiliate link
    }
  },

  /**
   * Calculates and returns counts of elements by their type.
   * It recursively goes through nested elements to account for all.
   *
   * @function
   * @name getElementCounts
   * @param {Object} state - The Vuex state object containing elements.
   * @returns {Object} An object with element types as keys and their respective counts as values.
   */
  getElementCounts(state) {
    const counts = {};
    const countElements = (els) => {
      Object.entries(els).forEach(([key, el]) => {
        // 'key' is the current key, and 'el' is the corresponding value
        counts[el.type] = (counts[el.type] || 0) + 1;
        if (el.elements?.length) {
          countElements(el.elements);
        }
      });
    };
    countElements(state.elementsById);
    return counts;
  },

  /**
   * Counts the number of elements of a specific type within a group.
   * @function
   * @name getCountsOfElementsByTypeInGroup
   * @param {Object} state - The Vuex state object containing elements.
   * @param {string} groupId - The ID of the group element.
   * @returns {Function} A function that takes an element type and returns the count of elements
   * of that type within the group.
   * */
  getCountsOfElementsByTypeInGroup: (state) => (groupId) => {
    const group = state.elementsById[groupId];
    const counts = {};

    if (group.elements) {
      group.elements.forEach((elementId) => {
        const element = state.elementsById[elementId];
        counts[element.type] = (counts[element.type] || 0) + 1;
      });
    }

    return counts;
  },

  /**
   * Computes the absolute position of an element based on its relative positions
   * within its parent elements.
   *
   * This function traverses through the array of parent IDs (including the element ID
   * itself) and accumulates the 'top' and 'left' styles to determine the absolute
   * position of the element.
   *
   * @function
   * @name getAbsolutePosition
   * @param {Object} state - The Vuex state object containing elements.
   * @param {string} elementId - The ID of the element for which the absolute position
   * is to be computed.
   * @param {Array<string>} parentsIdArray - An array of IDs representing the parent
   * elements.
   * @returns {Object} An object containing 'top', 'left', 'width', and 'height'
   * representing the absolute position and dimensions of the element.
   */
  getAbsolutePosition: (state) => (elementId, parentsIdArray) => {
    const position = [...parentsIdArray, elementId].reduce((memo, elId) => {
      const el = cloneDeep(state.elementsById[elId]) || {};
      return {
        top: memo.top + (el.styles?.top || 0),
        left: memo.left + (el.styles?.left || 0),
      };
    }, {
      top: 0,
      left: 0,
    });

    const element = state.elementsById[elementId];
    return {
      ...position,
      width: element?.styles?.width || 0,
      height: element?.styles?.height || 0,
    };
  },

  getQueuedElement(state) {
    return state.queuedElement;
  },

  getMediaSizeSummary(state) {
    return state.mediaSizeSummary;
  },

  getMediaElements(state) {
    // Returns only the media-relevant fields from elements that have image/video/background media.
    // Watched instead of the full elementsById map to avoid firing on unrelated changes
    // (position moves, text edits, style tweaks, etc.).
    return Object.values(state.elementsById)
      .filter((el) => (
        (el.type === 'image' && el.imageUrl)
        || (el.type === 'video' && el.src)
        || (el.backgroundImage && el.backgroundImageUrl)
        || el.data?.provider
      ))
      .map((el) => ({
        id: el.id,
        type: el.type,
        imageUrl: el.imageUrl,
        imageMeta: el.imageMeta ?? null,
        src: el.src,
        backgroundImage: el.backgroundImage,
        backgroundImageUrl: el.backgroundImageUrl,
        backgroundImageMeta: el.backgroundImageMeta ?? null,
        provider: el.data?.provider ?? null,
      }));
  },
};

/**
 * Elements actions object.
 * @type {object}
 */
export const elementsActions = {
  /**
   * Processes a given set of root elements and their nested children,
   * then constructs and commits the corresponding mappings of elements by ID and group.
   *
   * @function
   * @name setElementStoreValues
   * @param {Object} context - The Vuex action context.
   * @param {Object} payload - The payload containing elements and options.
   * @param {Array} payload.value - The root elements array.
   * @param {Object} [payload.options] - Additional options for the action and it's mutations.
   * @returns {void}
   */
  setElementStoreValues(context, payload) {
    const { commit, state } = context;
    const { value: rootElements, options } = payload;

    // preserve existing elementGroupMap entries that are not being updated
    const elementGroupMap = merge(
      {},
      state.elementGroupMap,
      {
        root: state.elementGroupMap.root.concat(rootElements.map((el) => el.id)),
      },
    );

    const elementsById = {};

    function processElements(elements) {
      elements.forEach((element) => {
        const {
          id,
          type,
          elements: nestedElements,
          ...otherProps
        } = cloneDeep(element);

        // make a copy of the default object to use as a base, in case new props
        // have been added since the user last edited their template
        const defaultElementObject = cloneDeep(defaultElements[type]);

        // fix for old text boxes, needs to happen before we start recording state mutations
        if (type === 'text' && otherProps.styles.maxSizeText > 0) {
          otherProps.maxSizeText = true;
        }

        // combine the default with the saved values, preferring the saved values
        elementsById[id] = {
          ...defaultElementObject,
          id,
          type,
          ...otherProps,
        };

        if (nestedElements) {
          elementGroupMap[id] = nestedElements.map((el) => el.id);
          processElements(nestedElements);
        }
      });
    }

    processElements(rootElements);

    // preserve existing elementsById entries that are not being updated
    const mergedElementsById = merge({}, state.elementsById, elementsById);

    commit('SET_ELEMENT_GROUP_MAP', { value: elementGroupMap, options });
    commit('SET_ELEMENTS_BY_ID', { value: mergedElementsById, options });
  },

  /**
   * Initiates the process of taking a history snapshot. The snapshot is primarily used for
   * undo/redo functionalities. While it's essentially a passthrough for the mutation, actions
   * are typically what is called from the templates, ensuring the Vuex paradigm is upheld.
   *
   * @function
   * @name takeHistorySnapshot
   * @param {Object} context - The Vuex action context.
   * @returns {void}
   */

  takeHistorySnapshot({ commit }) {
    commit('TAKE_HISTORY_SNAPSHOT');
  },

  /**
   * Sets the entire element group map in the state.
   * @function setElementGroupMap
   * @param {Object} context - Vuex action context.
   * @param {Object} payload - Contains data for the element group map.
   * @param {Object} payload.value - New element group map data.
   * @param {Object} [payload.options] - Options for the undo/redo plugin.
   */
  setElementGroupMap({ commit }, payload) {
    commit('SET_ELEMENT_GROUP_MAP', payload);
  },

  /**
   * Sets the elementGroupMap.root prop in the application state.
   * @function setElementGroupMapById
   * @param {Object} state - The state object.
   * @param {Object} payload - The payload containing the element group map.
   * @param {object} payload.value - The new state data.
   * @param {string} payload.value.id - The ID of the group.
   * @param {Array} payload.value.childIds - The the array of ids of the group's children.
   * @param {Object} [payload.options] - Options for the undo/redo plugin.
   */
  setElementGroupMapById({ commit }, payload) {
    commit('SET_ELEMENT_GROUP_MAP_BY_ID', payload);
  },

  /**
   * Action to set a single property value for a specific element.
   *
   * @function setOneElementValueByProp
   * @param {Object} context - Vuex action context object.
   * @param {Object} payload - The payload containing element property details.
   * @param {Object} payload.value - Details of the element property to update.
   * @param {string} payload.value.id - ID of the element.
   * @param {string} payload.value.propKey - The property key of the element.
   * @param {any} payload.value.propValue - The new value for the property.
   * @param {Object} [payload.options] - Options for plugins like undo/redo.
   *
   * Uses the local nested object path helper.
   */
  setOneElementValueByProp({ commit }, payload) {
    commit('SET_ONE_ELEMENT_VALUE_BY_PROP', payload);
  },

  /**
   * Sets multiple values of a specified element by their respective properties.
   * @function setManyElementValuesByProp
   * @param {Object} context - Vuex action context.
   * @param {Object} payload - Contains data for the element updates.
   * @param {Object} payload.value - Element data updates.
   * @param {string} payload.value.id - The ID of the element.
   * @param {Object} payload.value.pathValueMap - Map of property paths to values.
   * @param {Object} [payload.options] - Options for the undo/redo plugin.
   *
   * Uses the local nested object path helper.
   */
  setManyElementValuesByProp({ commit }, payload) {
    commit('SET_MANY_ELEMENT_VALUES_BY_PROP', payload);
  },

  /**
   * Action to delete a single property from a specific element.
   *
   * @function deleteOneElementProp
   * @param {Object} context - Vuex action context object.
   * @param {Object} payload - The payload containing element property details to delete.
   * @param {Object} payload.value - Details of the element property to delete.
   * @param {string} payload.value.id - ID of the element.
   * @param {string} payload.value.propKey - The property key to delete from the element.
   * @param {Object} [payload.options] - Options for plugins like undo/redo.
   *
   * Uses the local nested object path helper.
   */

  deleteOneElementProp({ commit }, payload) {
    commit('DELETE_ELEMENT_PROP', payload);
  },

  /**
   * Action to delete multiple properties from a specific element.
   *
   * @function deleteManyElementProps
   * @param {Object} context - Vuex action context object.
   * @param {Object} payload - Payload with element properties details to delete.
   * @param {Object} payload.value - Details of the element properties to delete.
   * @param {string} payload.value.id - ID of the element.
   * @param {Array<string>} payload.value.propPaths - Array of property keys or paths to delete.
   * @param {Object} [payload.options] - Options for plugins like undo/redo.
   *
   * Uses the local nested object path helper.
   */
  deleteManyElementProps({ commit }, payload) {
    commit('DELETE_ELEMENT_PROPS', payload);
  },

  // TODO: review whether these style and video sets are still needed with the local path helper.
  // need to refactor those bits in the components, but not a high priority
  /**
   * Sets the value of an element's styles property.
   * @function setElementStylesValueByProp
   * @param {Object} context - Vuex action context object.
   * @param {Object} payload - The payload containing element ID, property key, and property value.
   * @param {Object} payload.value - The element ID, property key, and property value object.
   * @param {Object} [payload.options] - Options for plugins like undo/redo.
   */
  setElementStylesValueByProp({ commit }, payload) {
    commit('SET_ELEMENT_STYLES_VALUE_BY_PROP', payload);
  },
  /**
   * Sets the value of an element's videoOptions property.
   * @function setElementVideoOptionsValueByPropP
   * @param {Object} context - Vuex action context object.
   * @param {Object} payload - The payload containing element ID, property key, and property value.
   * @param {Object} payload.value - The element ID, property key, and property value object.
   * @param {Object} [payload.options] - Options for plugins like undo/redo.
   */
  setElementVideoOptionsValueByProp({ commit }, payload) {
    commit('SET_ELEMENT_VIDEO_OPTIONS_VALUE_BY_PROP', payload);
  },

  /** .
   * Increments the value of an elements styles positions property.
   * @function incrementElementPosition
   * @param {Object} context - Vuex action context object.
   * @param {object} payload - The payload object.
   * @param {object} payload.value - The new state data.
   * @param {string} payload.value.id - The ID of the element.
   * @param {('top'|'bottom'|'left'|'right')} payload.value.positionType
   * - The position type to increment.
   * @param {Object} [payload.options] - Options for plugins like undo/redo.
   */
  incrementElementPosition({ commit }, payload) {
    commit('INCREMENT_ELEMENT_POSITION', payload);
  },
  /** .
   * Decrements the value of an elements styles positions property.
   * @function decrementElementPosition
   * @param {Object} context - Vuex action context object.
   * @param {object} payload - The payload object.
   * @param {object} payload.value - The new state data.
   * @param {string} payload.value.id - The ID of the element.
   * @param {('top'|'bottom'|'left'|'right')} payload.value.positionType
   * - The position type to increment.
   * @param {Object} [payload.options] - Options for plugins like undo/redo.
   */
  decrementElementPosition({ commit }, payload) {
    commit('DECREMENT_ELEMENT_POSITION', payload);
  },

  /**
   * Adds a new element to the state.
   *
   * @function addNewElement
   * @param {Object} context - The Vuex action context object.
   * @param {Object} payload - The payload containing details for the new element.
   * @param {Object} payload.value - The data related to the new element.
   * @param {string} payload.value.type - The type of the element.
   * @param {Object} [payload.value.settings] - Optional settings to apply to the new element.
   * @param {string} payload.value.parentId - The parent ID for nested elements.
   * @param {boolean} payload.value.isGroup - Indicates if the element is a group.
   * @param {boolean} payload.value.skipGroupRecalculation - Flag to skip group recalculation.
   * @param {number} [payload.value.position] - Index value of the location to insert new element.
   * @param {Object} [payload.options] - Options for plugins like undo/redo.
   * @returns {Object} The newly created element object.
   */
  addNewElement({ commit, dispatch, rootState }, payload) {
    const { value, options } = payload;
    const {
      type,
      settings,
      parentId,
      isGroup,
      skipGroupRecalculation,
      position, // optional: an index value of the location insert the new element
    } = value;

    const clone = cloneDeep(defaultElements[type] || {});

    if (settings) {
      merge(clone, settings);
    }

    // if we generated an id elsewhere, don't here (ex.ddg data mapping)
    clone.id = clone.id || crypto.randomUUID();

    if (clone.fixedParent) {
      // if we configure an element to have a fixed parent, assign the id of the element here
      // to use for the drag/drop checks
      clone.fixedParent = parentId || 'root';
    }

    const newElementPayload = {
      value: {
        groupKey: parentId || 'root',
        element: clone,
        position,
      },
      options,
    };

    commit('ADD_NEW_ELEMENT', newElementPayload);

    if (isGroup) {
      commit('SET_ELEMENT_GROUP_MAP_BY_ID', {
        value: {
          id: clone.id,
          childIds: [],
        },
      });
    }

    if (parentId && !skipGroupRecalculation) {
      commit('GROUP_AND_CHILD_BOUNDS_RECALCULATION', {
        value: {
          groupId: parentId,
        },
      });
    }

    if (rootState.panAndZoom.panMode) {
      dispatch('panAndZoom/setPanMode', false, { root: true });
    }

    return clone;
  },

  setQueuedElement({ commit, rootState, dispatch }, payload) {
    const { value = {}, options = { blacklist: true } } = payload;
    if (rootState.panAndZoom.panMode) {
      dispatch('panAndZoom/setPanMode', false, { root: true });
    }
    commit('SET_QUEUED_ELEMENT', { value, options });
  },

  /**
   * Duplicates an element in the state, including its nested children if it's a group.
   *
   * @function duplicateElement
   * @param {Object} context - The Vuex action context with state, dispatch, getters.
   * @param {Object} payload - Payload data.
   * @param {string} payload.value - ID of the element to duplicate.
   * @param {Object} [payload.options] - Options for plugins like undo/redo.
   */
  async duplicateElement({ state, dispatch, getters }, payload) {
    const { value: elementId } = payload;

    const parentId = getters.getElementParentGroupId(elementId);

    if (state.elementsById[parentId]?.type === 'social-data') {
      const namespace = parentId;
      const clone = cloneDeep(state.elementsById[elementId]);
      // give new id
      clone.id = crypto.randomUUID();
      await dispatch(`${namespace}/addDataElementAndMapping`, {
        element: {
          type: clone.type,
          key: clone.dataType,
          settings: clone,
        },
        parentId,
        namespace,
      }, { root: true });
      return;
    }

    async function duplicate(
      id,
      pId,
      {
        providerId = false,
      } = {},
      isChild,
    ) {
      // is it a group/parent element
      const isGroup = id !== 'root' && hasChildren(id, state.elementGroupMap);
      // clone
      const clone = cloneDeep(state.elementsById[id]);
      // give new id
      clone.id = crypto.randomUUID();
      if (!isChild) {
        clone.name += ' - COPY';
      }
      if (providerId && has(clone, 'data.provider')) {
        clone.data.provider = providerId;
      }

      // add element
      const addedElement = await dispatch('addNewElement', {
        value: {
          type: clone.type,
          isGroup,
          parentId: pId,
          settings: clone,
          skipGroupRecalculation: true,
        },
        options: {
          blacklist: true,
        },
      });

      if (addedElement.type === 'linked-data') {
        const [el1Id, el2Id] = state.elementGroupMap[id];
        const duplicate1 = await duplicate(el1Id, addedElement.id, { providerId });
        const duplicate2 = await duplicate(el2Id, addedElement.id, { providerId });

        dispatch('setOneElementValueByProp', {
          value: { id: duplicate1.id, propKey: 'data.linkedElement', propValue: duplicate2.id },
          options: { blacklist: true },
        });
        dispatch('setOneElementValueByProp', {
          value: { id: duplicate2.id, propKey: 'data.linkedElement', propValue: duplicate1.id },
          options: { blacklist: true },
        });
      } else if (isGroup) {
        const isAChild = true;
        // duplicate recursively
        [...state.elementGroupMap[id]].reverse().forEach((childId) => {
          duplicate(
            childId,
            clone.id,
            {
              providerId: getters.isDataProvider(addedElement.id) ? addedElement.id : providerId,
            },
            isAChild,
          );
        });
      }

      return addedElement;
    }

    await duplicate(elementId, parentId);
    dispatch('takeHistorySnapshot');
  },

  /**
   * Groups selected elements into a single group element. This is useful for bulk actions
   * on multiple elements or for nested grouping. Selected elements should be children of
   * the root to be grouped. The action computes the new group's dimensions and positions
   * based on the selected elements' styles and then creates a new group.
   *
   * @function groupSelectedElements
   * @param {Object} context - The Vuex action context.
   * @param {Object} context.state - The current state of this Vuex module.
   * @param {Object} context.rootState - The state of the root Vuex module.
   * @param {Function} context.dispatch - Vuex's dispatch function.
   * @param {Function} context.commit - Vuex's commit function.
   * @param {Object} payload - Payload data.
   * @param {Object} [payload.options] - Options for plugins like undo/redo.
   */
  async groupSelectedElements({
    commit, state, rootState, dispatch,
  }) {
    const { selectedElements } = rootState.templates;
    if (selectedElements.length < 1) {
      return; // jet there is nothing to group
    }

    // only group if the selected element is a child of root,
    // otherwise it's not valid to add it
    // sort it to the same order it has in the root group
    // TODO: other exceptions for things we don't want to allow grouping for
    const rootGroup = state.elementGroupMap.root;
    const childElements = selectedElements
      .filter((id) => rootGroup.includes(id) && !(id in state.elementGroupMap))
      .sort((a, b) => rootGroup.indexOf(a) - rootGroup.indexOf(b));

    if (selectedElements.length !== childElements.length) {
      toast.makeToast({
        heading: 'Helpful hint:',
        icon: 'info',
        hideAfter: 5000,
        stack: false,
        text: 'Only ungrouped elements can be grouped. Selected elements that are grouped, will be left in their current group. ',
      });
    }

    // Get an array of the indices in the allIds array corresponding to the selected IDs
    const indices = childElements.map((id) => rootGroup.indexOf(id));

    // Get the minimum index from the indices array
    const lowestIndexPosition = Math.min(...indices);

    // Look up the styles of each element
    const elementStyles = childElements.map((id) => {
      const { styles } = state.elementsById[id];
      return {
        id,
        styles: { ...styles },
      };
    });

    // Find the group dims and coordinates
    const groupSizeAndPosition = computeGroupSizeAndPosition(elementStyles);

    const newElement = await dispatch('addNewElement', {
      value: {
        type: 'group',
        // insert at the position of the top most valid selected element
        position: lowestIndexPosition,
        settings: {
          styles: { ...defaultElements.group.styles, ...groupSizeAndPosition },
        },
      },
      options: {
        blacklist: true,
      },
    });

    commit('CREATE_GROUP_FROM_SELECTION', {
      value: {
        groupKey: newElement.id,
        childElements,
      },
    });
  },

  /**
   * Ungroups elements from selected groups. This action looks through the state's
   * elementGroupMap to identify which groups are selected. Once identified, it
   * then commits a mutation to ungroup each of the selected groups.
   *
   * @function unGroupSelectedGroups
   * @param {Object} context - The Vuex action context.
   * @param {Object} context.state - The current state of this Vuex module.
   * @param {Object} context.rootState - The state of the root Vuex module.
   * @param {Function} context.commit - Vuex's commit function.
   * @param {Object} [payload.options] - Options for plugins like undo/redo.
   */
  unGroupSelectedGroups({ commit, state, rootState }) {
    const { selectedElements } = rootState.templates;

    // Define selectedGroups as an array of objects containing groupKey and groupParentKey
    const selectedGroups = Object.keys(state.elementGroupMap)
      .filter((groupParentKey) => state.elementGroupMap[groupParentKey]
        .some((groupKey) => selectedElements.includes(groupKey) && state.elementsById[groupKey]?.type === 'group'))
      .map((groupParentKey) => ({
        groupParentKey,
        groupKey: state.elementGroupMap[groupParentKey]
          .find((groupKey) => selectedElements.includes(groupKey)),
      }));

    // Commit the mutation for each selected group
    selectedGroups.forEach(({ groupParentKey, groupKey }) => {
      commit('UNGROUP_ALL_IN_GROUP', {
        value: {
          groupParentKey,
          groupKey,
        },
      });
    });
  },

  /**
   * Responds to moving or resizing an element. Triggers a mutation to recalculate
   * the bounds of groups and their child elements after an element has been moved or resized.
   *
   * @function onMoveOrResize
   * @param {Object} context - The Vuex action context.
   * @param {Function} context.commit - Vuex's commit function.
   * @param {Object} payload - The payload containing the group's data.
   * @param {Object} payload.value - The data related to the group.
   * @param {string} payload.value.groupId - The ID of the group being adjusted.
   * @param {Object} [payload.options] - Options for plugins like undo/redo.
   */
  onMoveOrResize({ commit }, payload) {
    commit('GROUP_AND_CHILD_BOUNDS_RECALCULATION', payload);
  },

  /**
   * Responds to an element being removed from a group. Triggers a mutation to
   * recalculate the bounds of the group after an element has been removed.
   *
   * @function whenElementRemovedGroup
   * @param {Object} context - The Vuex action context.
   * @param {Function} context.commit - Vuex's commit function.
   * @param {Object} payload - The payload containing the group's data.
   * @param {Object} payload.value - The data related to the group.
   * @param {string} payload.value.groupId - The ID of the affected group.
   * @param {Object} [payload.options] - Options for plugins like undo/redo.
   */
  whenElementRemovedGroup({ commit }, payload) {
    commit('GROUP_AND_CHILD_BOUNDS_RECALCULATION', payload);
  },

  /**
   * Responds to an element being added via draggable action. Triggers a mutation to
   * update the group's size and position after an element has been added.
   *
   * @function whenElementAddedViaDraggable
   * @param {Object} context - The Vuex action context.
   * @param {Function} context.commit - Vuex's commit function.
   * @param {Object} payload - The payload containing the element's and group's data.
   * @param {Object} payload.value - The data related to the group and element.
   * @param {string} payload.value.groupToId - The ID of the group to which the element is added.
   * @param {string} payload.value.elementAddedId - The ID of the added element.
   * @param {string} payload.value.groupFromId - The ID of the group from which the element came.
   * @param {Object} [payload.options] - Options for plugins like undo/redo.
   */
  whenElementAddedViaDraggable({
    state, commit, getters, dispatch,
  }, payload) {
    const { groupToId, elementAddedId } = payload.value;

    const newProvider = getters.isDataProvider(groupToId)
      ? groupToId : getters.getDataProvider(groupToId);

    const el = getters.getElementById(elementAddedId);
    if (has(el, 'data')) {
      if (el.data.provider === groupToId) {
        commit('WHEN_ELEMENT_ADDED_GROUP_VIA_DRAGGABLE', payload);
        return; // moved within provider (not nested), skip this
      }

      if (newProvider) {
        // keep mapping with updated provider data
        dispatch('setOneElementValueByProp', {
          value: {
            id: elementAddedId,
            propKey: 'data.provider',
            propValue: newProvider.id,
          },
        });
      } else {
        // otherwise remove it as mapped
        dispatch('deleteOneElementProp', {
          value: {
            id: elementAddedId,
            propKey: 'data',
          },
        });

        // if it's children also have data, remove them as well
        if (el.type === 'marquee') {
          const children = state.elementGroupMap[elementAddedId];
          children.forEach((childId) => {
            if (has(getters.getElementById(childId), 'data')) {
              dispatch('setOneElementValueByProp', {
                value: {
                  id: childId,
                  propKey: 'dataProvider',
                  propValue: null,
                },
              });
              dispatch('deleteOneElementProp', {
                value: {
                  id: childId,
                  propKey: 'data',
                },
              });
            }
          });
        }
      }
    }

    if (newProvider && el.type === 'marquee') {
      // keep mapping with updated provider data
      dispatch('setOneElementValueByProp', {
        value: {
          id: elementAddedId,
          propKey: 'data',
          propValue: {
            provider: newProvider,
            providerType: getters.getElementById(newProvider).type,
            range: '',
            filters: [{
              type: 'pagination',
              offset: 1, // Start from item... (index + 1)
              page: 1, // Page number (index + 1)
              limit: 10, // Items per page
            }],
          },
        },
      });
    }
    commit('WHEN_ELEMENT_ADDED_GROUP_VIA_DRAGGABLE', payload);
  },

  /**
   * Removes the specified element and its associated mappings. The function checks
   * for social data elements and takes appropriate actions (e.g., displaying warnings).
   * It recursively deletes children of group elements and removes any relevant data mappings.
   *
   * @function removeElementAndMappings
   * @param {Object} context - The Vuex action context.
   * @param {Function} context.commit - Vuex's commit function.
   * @param {Object} state - The current Vuex state.
   * @param {Object} rootState - The root state of the Vuex store.
   * @param {Function} getters - The Vuex getters.
   * @param {Object} payload - The payload containing information about the element to be removed.
   * @param {Object} payload.value - The element details.
   * @param {Object} payload.options - Additional options.
   */
  removeElementAndMappings({
    commit, state, rootState, getters, dispatch,
  }, payload) {
    const { value, options } = payload;
    const { groupKey = 'root', element } = value;
    const { id: elementId, type } = element;

    const hasProvider = element.data?.provider;
    const required = element.required || (hasProvider ? element.data.required : false);

    // if element is required, don't allow delete (fallback for keyboard delete)
    if (required) {
      toast.makeToast({
        heading: 'Information',
        icon: 'info',
        hideAfter: 4000,
        stack: false,
        text: element.requiredReason || 'This element is required.',
      });
      return;
    }
    /**
     * Recursively removes the specified group and its children.
     * Removes the group and all of its child elements from the state.
     *
     * @function removeGroup
     * @param {string} eid - The ID of the element (group) to be removed.
     * @param {string} gk - The group key indicating the parent group.
     * @param {object} opts - options during group removal
     */
    const removeGroup = (eid, gk) => {
      const theGroup = state.elementGroupMap[eid];

      // check group for required children
      // if so, warn user toast message and skip deletion of group
      const shouldSkipDelete = !getters.isDataProvider(eid)
        && theGroup.some((c) => state.elementsById[c].data?.required);

      if (shouldSkipDelete) {
        toast.makeToast({
          heading: 'Information',
          icon: 'info',
          hideAfter: 4000,
          stack: false,
          text: 'This group contains one or more required elements. Skipping.',
        });
      } else {
        // delete all the children
        // reverse for loop for removal because of mutation of group
        // while we are removing it's children so we don't fall out of the loop
        for (let i = theGroup.length - 1; i >= 0; i--) {
          const childId = theGroup[i];

          // if child is also a group recursive group removal
          if (state.elementGroupMap[childId]) {
            // pass fancy bits in as an option, so we can evaluate the children of any nested groups
            removeGroup(childId, eid);
          }
          commit('REMOVE_ELEMENT', { value: { groupKey: eid, elementId: childId }, options });
        }

        // delete element from group map
        // delete element
        commit('REMOVE_ELEMENT', { value: { groupKey: gk, elementId: eid }, options: { isGroupParent: true, ...options } });
        dispatch('unselectGroup', eid, { root: true });
      }
    };

    // If element is Linked, remove the ref from the element it was linked to
    // currently only for Sports Radar data
    if (element?.data?.linkedElement) {
      commit('SET_MANY_ELEMENT_VALUES_BY_PROP', {
        value: {
          id: element.data.linkedElement,
          pathValueMap: {
            'data.linkedElement': null,
          },
        },
        options,
      });
    }

    // if element is a parent of other elements
    if (state.elementGroupMap[elementId]) {
      removeGroup(elementId, groupKey);
    } else {
      // delete element from group map
      // delete element
      commit('REMOVE_ELEMENT', { value: { groupKey, elementId }, options });
    }

    ConduitEventsService.track(
      'graphics.deleted.element',
      {
        template_id: getters.getTemplateId,
        owner: getters.getTemplateOwner, // why do we have owner and owner id?
        type,
      },
      {
        ownerId: getters.getTemplateOwner,
        userId: this.state.user.id,
        isStaff: this.state.user.isStaff,
      },
    );

    // if element is still selected,
    // we need to clear its ID from the selection array or "fun" things happen
    if (rootState.templates.selectedElements.includes(elementId)) {
      dispatch('updateSelectedElements', elementId, { root: true });
    }
  },

  /**
   * Deletes the currently selected elements.
   *
   * For each selected element, it determines the parent group of the element,
   * then dispatches an action to remove the element and its associated mappings.
   * Once the element is removed, another action is dispatched to handle its removal from the group.
   * After processing all selected elements, it dispatches an action to clear the selection.
   *
   * @param {Object} context - The Vuex action context.
   * @param {Object} context.state - The local state of the module.
   * @param {Function} context.dispatch - The dispatch function to trigger actions.
   * @param {Object} context.rootState - The root state of the Vuex store.
   * @param {Object} context.getters - The getters available in the module.
   */
  async refreshMediaSizeSummary({ getters, commit }) {
    commit('SET_MEDIA_SIZE_SUMMARY', { loading: true, error: null });

    const toFetch = { staticImages: new Set(), videos: new Set(), mappedMedia: new Set() };

    getters.getMediaElements.forEach((el) => {
      const isMapped = !!el.provider;
      if (isMapped) {
        try {
          if (el.type === 'image') {
            const url = getters.isPropertyMapped(el.id, 'imageUrl')
              ? getters.getDataValue(el.id, 'imageUrl')
              : buildImageUrl(el.imageUrl, el.imageMeta);
            if (url) toFetch.mappedMedia.add(url);
          }
          if (el.type === 'video') {
            const url = getters.isPropertyMapped(el.id, 'src')
              ? getters.getDataValue(el.id, 'src')
              : el.src;
            if (url) toFetch.mappedMedia.add(url);
          }
          if (el.backgroundImage) {
            const url = getters.isPropertyMapped(el.id, 'backgroundImageUrl')
              ? getters.getBackgroundImageUrl(el.id)
              : buildImageUrl(el.backgroundImageUrl, el.backgroundImageMeta);
            if (url) toFetch.mappedMedia.add(url);
          }
        } catch {
          // provider data not yet loaded — skip this element
        }
      } else {
        if (el.type === 'image' && el.imageUrl) {
          toFetch.staticImages.add(buildImageUrl(el.imageUrl, el.imageMeta));
        }
        if (el.type === 'video' && el.src) toFetch.videos.add(el.src);
        if (el.backgroundImage && el.backgroundImageUrl) {
          toFetch.staticImages.add(buildImageUrl(el.backgroundImageUrl, el.backgroundImageMeta));
        }
      }
    });

    try {
      const [imgSizes, vidSizes, mappedSizes] = await Promise.all([
        getMediaSizes(toFetch.staticImages),
        getMediaSizes(toFetch.videos),
        getMediaSizes(toFetch.mappedMedia),
      ]);

      const summarize = (fetchedSizeMap) => {
        let totalBytes = 0;
        let knownCount = 0;
        let unknownCount = 0;
        fetchedSizeMap.forEach((bytes) => {
          if (bytes != null) {
            totalBytes += bytes;
            knownCount += 1;
          } else {
            unknownCount += 1;
          }
        });
        return { totalBytes, knownCount, unknownCount };
      };

      commit('SET_MEDIA_SIZE_SUMMARY', {
        loading: false,
        error: null,
        lastUpdated: Date.now(),
        staticImages: summarize(imgSizes),
        videos: summarize(vidSizes),
        mappedMedia: summarize(mappedSizes),
      });
    } catch (err) {
      commit('SET_MEDIA_SIZE_SUMMARY', { loading: false, error: err.message });
    }
  },

  deleteSelectedElements({
    state, dispatch, rootState, getters,
  }) {
    const { selectedElements } = rootState.templates;

    // selectedElements gets mutated while we are in the loop
    // so we must iterate through the loop backwards to prevent falling out
    for (let i = selectedElements.length - 1; i >= 0; i--) {
      const id = selectedElements[i];
      const elementParentGroupId = getters.getElementParentGroupId(id);

      dispatch('removeElementAndMappings', {
        value: {
          groupKey: elementParentGroupId,
          element: state.elementsById[id],
        },
      }).then(() => {
        dispatch('whenElementRemovedGroup', {
          value: {
            groupId: elementParentGroupId,
          },
        });
      });
    }
  },
};

/**
 * Elements mutations object.
 * @type {object}
 */
export const elementsMutations = {
  /**
  * we will be using mutation payload to set some undo/redo flags,
  *  so we always want to make an object with value being the traditional payload
  */

  /**
   * Mutates the entire element group map in the state.
   * @function SET_ELEMENT_GROUP_MAP
   * @param {Object} state - The state object.
   * @param {Object} payload - Contains data for the element group map.
   * @param {Object} payload.value - New element group map data.
   * @param {Object} [payload.options] - Options for the undo/redo plugin.
   */
  SET_ELEMENT_GROUP_MAP(state, payload) {
    const { value, options } = payload;
    state.elementGroupMap = value;
  },

  /**
   * Sets the elementGroupMap.root prop in the application state.
   * @function SET_ELEMENT_GROUP_MAP_BY_ID
   * @param {Object} state - The state object.
   * @param {Object} payload - The payload containing the element group map.
   * @param {object} payload.value - The new state data.
   * @param {string} payload.value.id - The ID of the group.
   * @param {Array} payload.value.childIds - The the array of ids of the group's children.
   * @param {Object} [payload.options] - Options for the undo/redo plugin.
   */
  SET_ELEMENT_GROUP_MAP_BY_ID(state, payload) {
    const { value, options } = payload;
    const { id, childIds } = value;
    setOne(state.elementGroupMap, id, childIds);
  },

  /**
   * Records a snapshot of the current state in the history for undo/redo functionalities.
   * As this application supports both edit and create workflows, the snapshot is captured
   * after a set of blacklisted transformations occur. This mutation primarily serves as a
   * trigger point for the history mechanisms in the undo/redo plugin.
   *
   * @function
   * @name TAKE_HISTORY_SNAPSHOT
   * @param {Object} state - The current Vuex state object. This is required by the undo/redo plugin
   * @returns {void}
   */
  // eslint-disable-next-line no-unused-vars
  TAKE_HISTORY_SNAPSHOT(state) {
  },

  /**
   * Sets the elementsById object in the application state.
   * @function SET_ELEMENT_GROUP_MAP
   * @param {Object} state - The state object.
   * @param {Object} payload - The payload containing the elements by ID.
   * @param {Object} payload.value - The elements by ID object.
   */
  SET_ELEMENTS_BY_ID(state, payload) {
    const { value, options } = payload;
    state.elementsById = value;
  },

  /**
   * Mutates multiple values of a specified element by their respective properties.
   * @function SET_MANY_ELEMENT_VALUES_BY_PROP
   * @param {Object} state - The state object.
   * @param {Object} payload - Contains data for the element updates.
   * @param {Object} payload.value - Element data updates.
   * @param {string} payload.value.id - The ID of the element.
   * @param {Object} payload.value.pathValueMap - Map of property paths to values.
   * @param {Object} [payload.options] - Options for the undo/redo plugin.
   */
  SET_MANY_ELEMENT_VALUES_BY_PROP(state, payload) {
    const { value, options } = payload;
    const { id, pathValueMap } = value;

    const element = state.elementsById[id];

    setMany(element, pathValueMap);
  },
  /**
   * Mutation to set a single property value for an element in the state.
   *
   * @function SET_ONE_ELEMENT_VALUE_BY_PROP
   * @param {Object} state - The state object.
   * @param {Object} payload - The payload containing element property details.
   * @param {Object} payload.value - Details of the element property to update.
   * @param {string} payload.value.id - ID of the element.
   * @param {string} payload.value.propKey - The property key of the element.
   * @param {any} payload.value.propValue - The new value for the property.
   * @param {Object} [payload.options] - Options for plugins like undo/redo.
   */
  SET_ONE_ELEMENT_VALUE_BY_PROP(state, payload) {
    const { value, options } = payload;
    const {
      id,
      propKey,
      propValue,
    } = value;

    const element = state.elementsById[id];

    setOne(element, propKey, propValue);
  },

  /**
   * Mutation to delete a single property from an element in the state.
   *
   * @function DELETE_ELEMENT_PROP
   * @param {Object} state - The state object.
   * @param {Object} payload - The payload containing element property details to delete.
   * @param {Object} payload.value - Details of the element property to delete.
   * @param {string} payload.value.id - ID of the element.
   * @param {string} payload.value.propKey - The property key to delete from the element.
   * @param {Object} [payload.options] - Options for plugins like undo/redo.
   */
  DELETE_ELEMENT_PROP(state, payload) {
    const { value, options } = payload;
    const { id, propKey } = value;
    const element = state.elementsById[id];

    deleteOne(element, propKey);
  },

  /**
   * Mutation to delete multiple properties from an element in the state.
   *
   * @function DELETE_ELEMENT_PROPS
   * @param {Object} state - The state object.
   * @param {Object} payload - Payload with element properties details to delete.
   * @param {Object} payload.value - Details of the element properties to delete.
   * @param {string} payload.value.id - ID of the element.
   * @param {Array<string>} payload.value.propPaths - Array of property keys or paths to delete.
   * @param {Object} [payload.options] - Options for plugins like undo/redo.
   */
  DELETE_ELEMENT_PROPS(state, payload) {
    const { value, options } = payload;
    const { id, propPaths } = value;
    const element = state.elementsById[id];

    deleteMany(element, propPaths);
  },

  /**
   * Sets the value of an element's styles property.
   * @function SET_ELEMENT_STYLES_VALUE_BY_PROP
   * @param {Object} state - The state object.
   * @param {Object} payload - The payload containing element ID, property key, and property value.
   * @param {Object} payload.value - The element ID, property key, and property value object.
   * @param {Object} [payload.options] - Options for plugins like undo/redo.
   */
  // TODO: remove all this deep nest mutations thanks to the set one many package
  SET_ELEMENT_STYLES_VALUE_BY_PROP(state, payload) {
    // TODO: some kind of prevention for adding non-bootstrapped
    // props to state object during a mutation
    const { value, options } = payload;
    const { id, propKey, propValue } = value;
    const { styles } = state.elementsById[id] || {};

    // reactively add if not exists
    if (!Object.prototype.hasOwnProperty.call(styles, propKey)) {
      styles[propKey] = propValue;
      return;
    }

    styles[propKey] = propValue;
  },

  /**
   * Sets the value of an element's videoOptions property.
   * @function SET_ELEMENT_VIDEO_OPTIONS_VALUE_BY_PROP
   * @param {Object} state - The state object.
   * @param {Object} payload - The payload containing the element ID, property key, and property value.
   * @param {Object} payload.value - The element ID, property key, and property value object.
   * @param {Object} [payload.options] - Options for plugins like undo/redo.
   */
  SET_ELEMENT_VIDEO_OPTIONS_VALUE_BY_PROP(state, payload) {
    // TODO: some kind of prevention for adding non-bootstrapped
    // props to state object during a mutation
    const { value, options } = payload;
    const { id, propKey, propValue } = value;
    state.elementsById[id].videoOptions[propKey] = propValue;
  },

  /**
   * Increments the position value of an element in the styles object.
   * @function INCREMENT_ELEMENT_POSITION
   * @param {object} state - The elements state object.
   * @param {object} payload - The payload object.
   * @param {object} payload.value - The new state data.
   * @param {string} payload.value.id - The ID of the element.
   * @param {Number} payload.value.stepSize - The amount you want to increment by (defaults to 1).
   * @param {('top'|'bottom'|'left'|'right')} payload.value.positionType
   * - The position type to increment.
   * @param {Object} [payload.options] - Options for plugins like undo/redo.
   */
  INCREMENT_ELEMENT_POSITION(state, payload) {
    const { value, options } = payload;
    const { id, positionType, stepSize } = value;
    state.elementsById[id].styles[positionType] += stepSize || 1;
  },

  /**
   * Decrements the position value of an element in the styles object.
   * @function DECREMENT_ELEMENT_POSITION
   * @param {object} state - The elements state object.
   * @param {object} payload - The payload object.
   * @param {object} payload.value - The new state data.
   * @param {string} payload.value.id - The ID of the element.
   * @param {Number} payload.value.stepSize - The amount you want to decrement by (defaults to 1).
   * @param {('top'|'bottom'|'left'|'right')} payload.value.positionType
   * - The position type to increment.
   * @param {Object} [payload.options] - Options for plugins like undo/redo.
   */
  DECREMENT_ELEMENT_POSITION(state, payload) {
    const { value, options } = payload;
    const { id, positionType, stepSize } = value;
    state.elementsById[id].styles[positionType] -= stepSize || 1;
  },

  /**
   * Ungroups all elements within a specified group.
   *
   * For the specified group, it determines the positions of its child elements
   * relative to the group and updates their positions. Then, the child elements
   * are added directly to the group's parent while removing the group. The group itself
   * is then deleted from the state.
   *
   * @param {Object} state - The local state of the module.
   * @param {Object} payload - The payload provided to the mutation.
   * @param {Object} payload.value - The details about the group to ungroup.
   * @param {string} payload.value.groupParentKey - The parent group's key.
   * @param {string} payload.value.groupKey - The key of the group to ungroup.
   * @param {Object} [payload.options] - Any additional options for the mutation.
   */
  UNGROUP_ALL_IN_GROUP(state, payload) {
    const { value, options } = payload;
    const { groupParentKey, groupKey } = value;
    const parentFallback = groupParentKey || 'root'; // for later when we let groups in groups

    const elementGroupIndex = state.elementGroupMap[parentFallback].indexOf(groupKey);
    const childElements = state.elementGroupMap[groupKey];

    childElements.forEach((id) => {
      const elStyles = state.elementsById[id].styles;
      const gStyles = state.elementsById[groupKey].styles;
      const newPosition = getPositionRelativeToGroupParent(elStyles, gStyles);

      setMany(state.elementsById[id], {
        'styles.top': newPosition.top,
        'styles.left': newPosition.left,
      });
    });

    // remove and insert children at the same posistion
    state.elementGroupMap[parentFallback].splice(elementGroupIndex, 1, ...childElements);
    // delete the element
    deleteOne(state.elementsById, groupKey);
  },

  /**
   * Adds a new element to the state and, optionally, maps it to its parent group.
   *
   * The mutation adds the given element to the elementsById collection and, based
   * on the options provided, maps the element to the specified group in the
   * elementGroupMap. The element can be mapped to the beginning, end, or a
   * specific position within the group.
   *
   * @param {Object} state - Vuex state object.
   * @param {Object} payload - Contains value and options for the mutation.
   * @param {Object} payload.value - Contains groupKey, element, and position.
   * @param {string} payload.value.groupKey - Key of the parent group.
   * @param {Object} payload.value.element - Element data to be added.
   * @param {string|number} [payload.value.position] - Position in the group.
   * @param {Object} [payload.options] - Contains skipMapping flag along with undo/redo options.
   * @param {boolean} [payload.options.skipMapping] - If true, skips element mapping.
   */
  ADD_NEW_ELEMENT(state, payload) {
    const { value, options = {} } = payload;
    const { groupKey, element, position } = value;
    // Do group map and element mutations here so history sees them as a single mutation
    // add the element to the elment by id array
    state.elementsById[element.id] = element;
    if (!options.skipMapping) {
      // add the mapping
      if (position) {
        if (position === 'end') {
          state.elementGroupMap[groupKey].push(element.id);
        } else {
          state.elementGroupMap[groupKey].splice(position, 0, element.id);
        }
      } else {
        state.elementGroupMap[groupKey].unshift(element.id);
      }
    }
  },

  SET_QUEUED_ELEMENT(state, payload) {
    const { value, options } = payload;
    state.queuedElement = value;
  },

  /**
   * Removes an element from the state and its associated mappings.
   *
   * This mutation first removes the element from its parent group's mapping array
   * in the elementGroupMap collection. Then, it deletes the element from the
   * elementsById collection. If the element is a group parent itself, its mapping
   * is also removed from the elementGroupMap.
   *
   * @param {Object} state - Vuex state object.
   * @param {Object} payload - Contains value and options for the mutation.
   * @param {Object} payload.value - Contains groupKey and elementId.
   * @param {string} payload.value.groupKey - Key of the parent group.
   * @param {string} payload.value.elementId - ID of the element to be removed.
   * @param {Object} [payload.options] - Contains isGroupParent flag.
   * @param {boolean} [payload.options.isGroupParent] - If true, removes group mapping.
   */
  REMOVE_ELEMENT(state, payload) {
    const { value, options } = payload;
    const { groupKey, elementId } = value;

    // remove from it's parent's mapping array
    const elementGroupIndex = state.elementGroupMap[groupKey].indexOf(elementId);
    state.elementGroupMap[groupKey].splice(elementGroupIndex, 1); // remove
    // delete the element
    deleteOne(state.elementsById, elementId);
    if (options?.isGroupParent) {
      deleteOne(state.elementGroupMap, elementId);
    }
  },

  /**
   * Creates a group from a selection of elements.
   *
   * This mutation takes multiple selected elements and groups them together.
   * The elements are first removed from the root group, and their positions
   * are updated to be relative to the new parent group.
   *
   * If the element being grouped is itself a parent of other elements (i.e.,
   * a group), its children's positions would also need to be updated relative
   * to their parent's new position in the new group. However, as of now,
   * nested groups (groups within groups) are not supported, so this case is not handled.
   *
   * @param {Object} state - Vuex state object.
   * @param {Object} payload - Contains value and options for the mutation.
   * @param {Object} payload.value - Contains groupKey and childElements.
   * @param {string} payload.value.groupKey - Key of the new group being created.
   * @param {Array<string>} payload.value.childElements - IDs of the elements to be grouped.
   * @param {Object} [payload.options] - - Options for plugins like undo/redo.
   */
  CREATE_GROUP_FROM_SELECTION(state, payload) {
    const { value, options } = payload;
    const { groupKey, childElements } = value;
    const { elementGroupMap } = state;

    // remove items from root group before adding them to the new group
    elementGroupMap.root = elementGroupMap.root
      .filter((id) => !childElements.includes(id));

    // update the posistions of the elements to be relative to the group
    function updateChildPositionRelativeToGroup(element, groupTop, groupLeft) {
      // Update the element's position to be relative to the new parent (group)
      setMany(element, {
        'styles.top': element.styles.top - groupTop,
        'styles.left': element.styles.left - groupLeft,
      });

      //  we don't do groups in groups yet,
      // but keeping this block around as a reminder for when we do.
      // // If the element is also a group, update its children's positions
      // if (elementGroupMap[element.id]) {
      //   const kids = elementGroupMap[element.id];
      //   kids.forEach((id) => {
      //     const { top, left } = element.styles;
      //     updateChildPositionRelativeToGroup(state.elementsById[id], top, left);
      //   });
      // }
    }

    const newGroupTop = state.elementsById[groupKey].styles.top;
    const newGroupLeft = state.elementsById[groupKey].styles.left;

    childElements.forEach((id) => {
      updateChildPositionRelativeToGroup(state.elementsById[id], newGroupTop, newGroupLeft);
    });

    elementGroupMap[groupKey] = childElements;
  },

  /**
   * Recalculates the bounds of groups and their child elements. This mutation
   * handles repositioning and resizing after a group's elements have been moved or resized.
   *
   * @function GROUP_AND_CHILD_BOUNDS_RECALCULATION
   * @param {Object} state - The current state of this Vuex module.
   * @param {Object} payload - The payload containing the group's data.
   * @param {Object} payload.value - The data related to the group.
   * @param {string} payload.value.groupId - The ID of the group being adjusted.
   * @param {Object} [payload.options] - Options for plugins like undo/redo.
   */
  GROUP_AND_CHILD_BOUNDS_RECALCULATION(state, payload) {
    const { value, options } = payload;
    const { groupId } = value;
    const groupEl = state.elementsById[groupId];
    if (!groupEl) return; // nothing to do, so bounce
    const shouldSkip = [
      'social-data',
      'sheet-data',
      'sportradar',
      'linked-data',
      'slideshow',
      'marquee',
    ].some((type) => type === groupEl.type);

    if (groupId === 'root' || shouldSkip) return; // nothing to do, so bounce

    const childElements = state.elementGroupMap[groupId];

    // get child position styles relative to parent
    const relToGrandParent = childElements.map((id) => {
      const { styles } = state.elementsById[id];
      const newPos = getPositionRelativeToGroupParent({ ...styles }, groupEl.styles);
      return {
        id,
        styles: { ...styles, ...newPos },
      };
    });

    // calc new size of group
    const updatedGroupSize = (relToGrandParent.length)
      ? computeGroupSizeAndPosition(relToGrandParent) : {
        top: 0,
        bottom: 0,
        width: 0,
        height: 0,
      };

    // update group position
    setMany(groupEl, {
      'styles.top': updatedGroupSize.top,
      'styles.left': updatedGroupSize.left,
      'styles.width': updatedGroupSize.width,
      'styles.height': updatedGroupSize.height,
    });

    relToGrandParent.forEach((el) => {
      // reset element rel to group
      setMany(state.elementsById[el.id], {
        'styles.top': el.styles.top - groupEl.styles.top,
        'styles.left': el.styles.left - groupEl.styles.left,
      });
    });
  },

  /**
   * Handles the changes in the state when an element is added to a group via a draggable action.
   * Updates the group's and the element's positions.
   *
   * @function WHEN_ELEMENT_ADDED_GROUP_VIA_DRAGGABLE
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The payload containing the element's and group's data.
   * @param {Object} payload.value - The data related to the group and element.
   * @param {string} payload.value.groupToId - The ID of the group to which the element is added.
   * @param {string} payload.value.elementAddedId - The ID of the added element.
   * @param {string} payload.value.groupFromId - The ID of the group from which the element came.
   * @param {Object} [payload.options] - Options for plugins like undo/redo.
   */
  WHEN_ELEMENT_ADDED_GROUP_VIA_DRAGGABLE(state, payload) {
    const { value, options } = payload;
    const { groupToId, elementAddedId, groupFromId } = value;
    const groupToEl = state.elementsById[groupToId];
    const groupFromEl = state.elementsById[groupFromId];
    const addedElement = state.elementsById[elementAddedId];

    let addedElStyles = { ...addedElement.styles };

    const goingToDataProviderLayer = [
      'social-data',
      'sheet-data',
      'sportradar',
      'linked-data',
      'slideshow', // not exactly data, except it is on the producer side
    ].some((type) => type === groupToEl?.type);

    if (goingToDataProviderLayer) {
      const newPos = getPositionRelativeToGroupParent({ ...addedElStyles }, groupFromEl?.styles);
      addedElStyles = { ...addedElStyles, ...newPos };

      // set el rel to root
      setMany(addedElement, {
        'styles.top': addedElStyles.top,
        'styles.left': addedElStyles.left,
      });
      return;
    }

    if (groupFromId !== 'root') {
      // TODO: moving in and out of nested groups may need some more logic,
      // but just want to get the basics and see how it goes
      const newPos = getPositionRelativeToGroupParent({ ...addedElStyles }, groupFromEl?.styles);
      addedElStyles = { ...addedElStyles, ...newPos };
    }

    // update positions
    if (groupToId !== 'root') {
      const childElements = state.elementGroupMap[groupToId];

      // get child position styles relative to parent
      const relToGrandParent = childElements.map((id) => {
        if (id === elementAddedId) {
          return {
            id,
            styles: addedElStyles,
          };
        }

        const { styles } = state.elementsById[id];
        const newPos = getPositionRelativeToGroupParent({ ...styles }, groupToEl?.styles);
        return {
          id,
          styles: { ...styles, ...newPos },
        };
      });

      // calc new size of group
      const updatedGroupSize = computeGroupSizeAndPosition(relToGrandParent);
      // reset group size
      setMany(groupToEl, {
        'styles.top': updatedGroupSize.top,
        'styles.left': updatedGroupSize.left,
        'styles.width': updatedGroupSize.width,
        'styles.height': updatedGroupSize.height,
      });

      relToGrandParent.forEach((el) => {
        // reset element rel to group
        setMany(state.elementsById[el.id], {
          'styles.top': el.styles.top - groupToEl.styles.top,
          'styles.left': el.styles.left - groupToEl.styles.left,
        });
      });
    } else {
      // set el rel to root
      setMany(addedElement, {
        'styles.top': addedElStyles.top,
        'styles.left': addedElStyles.left,
      });
    }
  },

  SET_MEDIA_SIZE_SUMMARY(state, payload) {
    Object.assign(state.mediaSizeSummary, payload);
  },
};
